import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import BrandLogo from '../../components/BrandLogo/BrandLogo';
import { NAVY } from './websiteTokens';
import { useWebsiteTheme } from './useWebsiteTheme';
import ThemeToggle from './components/ThemeToggle';

const BRAND = 'EzeitPro';
const COMPANY = 'UtilStop LLC-FZ';
const CONTACT_EMAIL = 'sales@ezeitpro.com';
const WEBSITE = 'www.ezeitpro.com';

const offices = [
  {
    name: 'UtilStop L.L.C-FZ',
    region: 'United Arab Emirates',
    phones: ['+971 56 135 8594', '+971 56 122 0761'],
    hours: '9:00 AM – 5:00 PM (Dubai Time)',
    address: [
      '38 Tanvi Business Center',
      'Horizon Building, Port Saeed, Opposite DIB',
      'Dubai, U.A.E',
    ],
  },
  {
    name: 'UtilStop India Private Limited',
    region: 'India',
    phones: ['+91 99940 06517'],
    hours: '9:00 AM – 6:00 PM (IST)',
    address: [
      'Awfis Space Solutions,',
      '7th Floor RR Tower 4, Thiru Vi Ka Industrial Estate,',
      'SIDCO Industrial Estate, Guindy, Chennai, Tamil Nadu 600032',
    ],
  },
];

export default function ContactUs() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useWebsiteTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);


  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const d = isDark ? {
    bg: NAVY.bg, surface: NAVY.surface, bgAlt: NAVY.bgAlt,
    text: NAVY.text, textSec: NAVY.textSec, textMuted: NAVY.textMuted,
    border: NAVY.border,
    primary: '#E91E63', accent: 'rgba(233,30,99,0.18)',
    footBg: NAVY.footBg,
    sh: '0 1px 4px rgba(3,8,20,0.6)',
  } : {
    bg: '#FAFAFA', surface: '#FFFFFF', bgAlt: '#F5F5F5',
    text: '#212121', textSec: '#424242', textMuted: '#757575',
    border: 'rgba(0,0,0,0.08)',
    primary: '#C2185B', accent: '#FCE4EC',
    footBg: '#212121',
    sh: '0 1px 4px rgba(0,0,0,0.06)',
  };

  // Defined inside so they close over d and isMobile — same pattern as the legal pages.
  const IconBubble = ({ children }) => (
    <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: d.accent, color: d.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {children}
    </div>
  );

  const Card = ({ children, style }) => (
    <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '16px', padding: isMobile ? '20px' : '26px', boxShadow: d.sh, ...style }}>
      {children}
    </div>
  );

  const linkStyle = { color: d.primary, textDecoration: 'none', fontWeight: 600, fontSize: isMobile ? '14px' : '15px' };

  return (
    <div style={{ fontFamily: "'Roboto','Segoe UI',Arial,sans-serif", background: d.bg, color: d.text, minHeight: '100vh', transition: 'background 0.3s,color 0.3s', overflowX: 'hidden' }}>
      <Helmet>
        <title>Contact Us | {BRAND}</title>
        <meta name="description" content={`Get in touch with ${BRAND} — our offices, email and phone numbers in the UAE and India.`} />
      </Helmet>

      {/* Header */}
      <header style={{ background: d.surface, borderBottom: `1px solid ${d.border}`, padding: `0 ${isMobile ? '16px' : '32px'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px', position: 'sticky', top: 0, zIndex: 100, boxShadow: d.sh }}>
        <BrandLogo height={36} tone={isDark ? 'dark' : 'light'} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ThemeToggle isDark={isDark} onToggle={toggleTheme} color={d.text} border={d.border} size={36} />
          <button
            onClick={() => navigate('/')}
            style={{ background: d.bgAlt, border: `1px solid ${d.border}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: d.textSec, fontWeight: 500, whiteSpace: 'nowrap' }}
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Banner */}
      <div style={{ background: isDark ? 'linear-gradient(135deg,#1a0a2e 0%,#16213e 100%)' : `linear-gradient(135deg,${d.accent} 0%,#f5f5f5 100%)`, padding: isMobile ? '36px 20px 30px' : '48px 32px 40px', textAlign: 'center', borderBottom: `1px solid ${d.border}` }}>
        <div style={{ display: 'inline-block', background: d.primary, color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>We are here to help</div>
        <h1 style={{ fontSize: isMobile ? '26px' : '36px', fontWeight: 700, color: d.text, margin: '0 0 10px' }}>Contact Us</h1>
        <p style={{ fontSize: isMobile ? '14px' : '15px', color: d.textSec, margin: '0 auto', maxWidth: '620px', lineHeight: 1.75 }}>
          Schedule a call or reach out to us for any queries regarding our services. Leave your details and we
          will be in touch as soon as possible. We will be happy to connect with you!
        </p>
      </div>

      {/* Body */}
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: isMobile ? '28px 16px 60px' : '48px 24px 80px' }}>

        {/* Direct channel */}
        <Card style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: isMobile ? '17px' : '19px', fontWeight: 700, color: d.text, margin: '0 0 18px' }}>Direct Channel</h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
              <IconBubble><Mail size={17} /></IconBubble>
              <div>
                <div style={{ fontSize: '12px', color: d.textMuted, fontWeight: 600, marginBottom: '3px' }}>Email</div>
                <a href={`mailto:${CONTACT_EMAIL}`} style={linkStyle}>{CONTACT_EMAIL}</a>
                <div style={{ fontSize: '12px', color: d.textMuted, marginTop: '4px' }}>We respond within 24 hours</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
              <IconBubble><Phone size={17} /></IconBubble>
              <div>
                <div style={{ fontSize: '12px', color: d.textMuted, fontWeight: 600, marginBottom: '3px' }}>Phone</div>
                <a href="tel:+971561358594" style={linkStyle}>+971 56 135 8594</a>
                <div style={{ fontSize: '12px', color: d.textMuted, marginTop: '4px' }}>9:00 AM – 5:00 PM (Dubai Time)</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Offices */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
          {offices.map((office) => (
            <Card key={office.name}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: d.primary, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>{office.region}</div>
              <h3 style={{ fontSize: isMobile ? '16px' : '17px', fontWeight: 700, color: d.text, margin: '0 0 18px' }}>{office.name}</h3>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '16px' }}>
                <IconBubble><MapPin size={17} /></IconBubble>
                <div>
                  <div style={{ fontSize: '12px', color: d.textMuted, fontWeight: 600, marginBottom: '3px' }}>Address</div>
                  <p style={{ fontSize: isMobile ? '13px' : '14px', color: d.textSec, lineHeight: 1.7, margin: 0 }}>
                    {office.address.map((line) => <React.Fragment key={line}>{line}<br /></React.Fragment>)}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '16px' }}>
                <IconBubble><Phone size={17} /></IconBubble>
                <div>
                  <div style={{ fontSize: '12px', color: d.textMuted, fontWeight: 600, marginBottom: '3px' }}>Phone</div>
                  {office.phones.map((phone) => (
                    <a key={phone} href={`tel:${phone.replace(/\s/g, '')}`} style={{ ...linkStyle, display: 'block', fontSize: isMobile ? '13px' : '14px', marginBottom: '2px' }}>{phone}</a>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <IconBubble><Clock size={17} /></IconBubble>
                <div>
                  <div style={{ fontSize: '12px', color: d.textMuted, fontWeight: 600, marginBottom: '3px' }}>Working Hours</div>
                  <p style={{ fontSize: isMobile ? '13px' : '14px', color: d.textSec, lineHeight: 1.7, margin: 0 }}>{office.hours}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Email CTA */}
        <div style={{ background: d.surface, border: `2px solid ${d.primary}`, borderRadius: '16px', padding: isMobile ? '22px 16px' : '30px 24px', textAlign: 'center', marginTop: '20px' }}>
          <div style={{ fontSize: isMobile ? '13px' : '14px', color: d.textSec, marginBottom: '14px', lineHeight: 1.7 }}>
            Have a question about {BRAND}? Write to our team and we will get back to you.
          </div>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Enquiry — ${BRAND}`}
            style={{ display: 'inline-block', background: d.primary, color: '#fff', padding: '12px 28px', borderRadius: '28px', textDecoration: 'none', fontWeight: 600, fontSize: isMobile ? '14px' : '15px', wordBreak: 'break-all' }}
          >
            {CONTACT_EMAIL}
          </a>
          <div style={{ fontSize: '12px', color: d.textMuted, marginTop: '12px' }}>{WEBSITE}</div>
        </div>

      </div>

      {/* Footer */}
      <footer style={{ background: d.footBg, color: '#9E9E9E', padding: '24px 32px', textAlign: 'center', fontSize: '13px' }}>
        <p style={{ margin: 0 }}>
          © 2026 {COMPANY}, Dubai. All rights reserved. &nbsp;·&nbsp;
          <a href="/privacy-policy" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Privacy</a> &nbsp;·&nbsp;
          <a href="/terms" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Terms</a> &nbsp;·&nbsp;
          <a href="/security" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Security</a>
        </p>
      </footer>
    </div>
  );
}
